'use client';

import { c as _c } from "react/compiler-runtime";
import { ChartIntentProvider } from '@/providers/chart-intent-provider';

// `@morfi/backend-sdk-v2` provider — mounts the v2 SDK runtime (cache,
// auth/JWT mint, connectivity FSM, devtool module-singletons) AND composes
// all auth/wallet providers internally so `layout.tsx` has no auth concerns.
//
// Internal composition:
//   SDKProvider (v2 core, keyed by network)
//     AuthSyncProvider (OAuth cross-provider sync)
//     SdkAuthProvider (session restore / logout / auth store mirror — SDK-owned)
//       TurnkeyProvider → SdkTurnkeyWebProvider (morfi config bridge)
//         ActiveWalletBridge (Zustand → SDK active-wallet context)
//           SdkWalletsProvider (Turnkey wallet data via TanStack)
//             SdkImpersonationProvider (grant hydration + TTL monitor + overlay)
//             SdkIdentityKeySync (inline: mirrors useAuth userId → SDKProvider.identityKey)
//             {children}
//
// See `frontend/.claude/skills/sdk-web-usage-guide` (provider setup) and
// `sdk-debug-web` (the HTTP devtool bridge).

import type { ImpersonationActivatePayload, ImpersonationStoreMutators, ImpersonationStoreSlice, TeardownReason } from '@morfi/backend-sdk-v2/client';
import { ActiveWalletProvider, AuthSyncProvider, type BlockhashResolverMode, createWebDefaultAdapter, type GiftingHostBindings, GiftingHostProvider, isBlockhashResolverMode, MarketConfigProvider, SDKProvider, type SDKProviderProps, SdkAuthProvider, type SdkAuthProviderCallbacks, SdkImpersonationProvider, SdkTurnkeyWebProvider, SdkWalletsProvider, useAuth } from '@morfi/backend-sdk-v2/client';
import { type ReactNode, useEffect, useState, useSyncExternalStore } from 'react';
import { useOnboardingStore } from '@/app/[locale]/onboarding/_hooks/use-onboarding-store';
import { LOGO_SRC_DARK_BACKGROUND, LOGO_SRC_LIGHT_BACKGROUND } from '@/components/logo';
import { TOAST_IDS } from '@/config/toast-ids';
import { onEvent } from '@/events';
import { useClaimGift } from '@/hooks/gifts/use-claim-gift';
import { useSendUsdcGift } from '@/hooks/gifts/use-send-gift';
import { useSendOutcomeTokenGift } from '@/hooks/gifts/use-send-outcome-token-gift';

// Withdraw flow removed in the new contract — no claw-back. We bind a
// no-op stub so the SDK's GiftingHostBindings shape stays satisfied.
function useWithdrawGift(): {
  step: 'idle';
  error: null;
  submit: () => Promise<null>;
  reset: () => void;
} {
  return {
    step: 'idle',
    error: null,
    async submit(): Promise<null> {
      return null;
    },
    reset(): void {}
  };
}
import { env } from '@/lib/config/env';
import { installApiInstrumentation } from '@/lib/dev/api-instrument';
import { installImageInstrumentation } from '@/lib/dev/image-instrument';
import '@/lib/device-id/fetch-wrapper';
import { translateToast } from '@/lib/i18n/toast-translator';
import '@/lib/impersonation/fetch-wrapper';
import { useImpersonationStore } from '@/lib/impersonation/store';
import { teardownImpersonation } from '@/lib/impersonation/teardown';
import { useUserNetwork } from '@/lib/network/client';
import { toSdkApiBase } from '@/lib/network/sdk-api-base';
import type { Network } from '@/lib/network/types';
import { useNetworkConfig } from '@/lib/network/use-network-config';
import { reportError } from '@/lib/telemetry';
import { resetIdentity } from '@/lib/telemetry/identify';
import { logger } from '@/lib/telemetry/logger';
import { reportLoginError } from '@/lib/telemetry/login-error';
import { reportNatsStartup } from '@/lib/telemetry/nats-startup';
import { reportPopupCallbackSkip } from '@/lib/telemetry/popup-callback-skip';
import { dismissToast, showToast } from '@/lib/ui/toast';
import { getOauthRedirectUri, TURNKEY_API_BASE_URL, TURNKEY_AUTH_PROXY_CONFIG_ID, TURNKEY_BORDER_RADIUS, TURNKEY_DARK_PRIMARY_HEX, TURNKEY_LIGHT_PRIMARY_HEX, TURNKEY_ORG_ID, TURNKEY_X_CLIENT_ID } from '@/lib/wallet/turnkey';
import { AnalyticsProvider } from '@/providers/analytics-provider';
import { IdentityProvider } from '@/providers/identity-provider';
import { useActiveWalletStore } from '@/stores/active-wallet';
import { useNsfwRevealStore } from '@/stores/nsfw-reveal';

// ── Dev instrumentation ───────────────────────────────────────────────────
// Dev-only: patch `globalThis.fetch` to record API calls into the DevToolkit
// "API" tab. The literal `process.env.NODE_ENV` is constant-folded by
// Webpack/Turbopack — in a production build this entire `if` block is
// dead-code-eliminated, the function references become unused, and the imports
// are tree-shaken out of the bundle. Zero prod overhead. A top-level `if`
// (rather than a const+ternary) is the canonical DCE-safe form.
//
// The impersonation global-`fetch` wrapper is installed for side-effect via the
// `@/lib/impersonation/fetch-wrapper` import above (kept in its own module so it
// can be unit-tested in isolation).
if (process.env.NODE_ENV !== 'production') {
  installApiInstrumentation();
  installImageInstrumentation();
}

// The production-style local harness loads the code-split DevToolkit bridge
// after SDKProvider mounts. Enable its metadata-only recorder here so the
// provider's first effect can subscribe before any proof trade occurs.
if (process.env.NODE_ENV === 'production' && env.NEXT_PUBLIC_DEV_TOOLKIT === '1' && typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
  (globalThis as {
    __MORFI_DEVTOOL_ENABLED__?: boolean;
  }).__MORFI_DEVTOOL_ENABLED__ = true;
}

// ── Identity key bridge ────────────────────────────────────────────────────
// Module-level store: the inner auth tree (SdkAuthProvider → useAuth) pushes
// the current userId in; SDKProvider reads it back via identityKey so a
// login/logout re-mints the JWT without a full remount.
//
// This pattern is preserved from `sdk-identity-bridge.tsx` (now deleted).
// The store is module-scoped so it survives across React strict-mode cycles.

let _currentUserId: string | null = null;
const _identityListeners: Set<() => void> = new Set();
function setIdentityKey(userId: string | null): void {
  if (_currentUserId === userId) return;
  _currentUserId = userId;
  for (const fn of _identityListeners) fn();
}
function subscribeIdentity(listener: () => void): () => void {
  _identityListeners.add(listener);
  return (): void => {
    _identityListeners.delete(listener);
  };
}
function getIdentitySnapshot(): string | null {
  return _currentUserId;
}

/**
 * Side-effect-only component: reads `useAuth()` from inside the SdkAuthProvider
 * tree and mirrors the userId into the module store so `SDKProvider`
 * (which wraps SdkAuthProvider) can read it via `identityKey`.
 */
function SdkIdentityKeySync() {
  const $ = _c(3);
  const {
    user
  } = useAuth();
  const userId = user?.id ?? null;
  let t0;
  let t1;
  if ($[0] !== userId) {
    t0 = () => {
      setIdentityKey(userId);
    };
    t1 = [userId];
    $[0] = userId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return null;
}

// ── Turnkey provider ──────────────────────────────────────────────────────
// Gathers morfi-specific config (org IDs, theme, analytics callbacks) and
// hands it to the SDK.

function TurnkeyProviderInner(t0) {
  const $ = _c(4);
  const {
    children
  } = t0;
  const [isDark] = useState(_temp);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      organizationId: TURNKEY_ORG_ID,
      apiBaseUrl: TURNKEY_API_BASE_URL,
      authProxyConfigId: TURNKEY_AUTH_PROXY_CONFIG_ID,
      oauthRedirectUri: getOauthRedirectUri(),
      xClientId: TURNKEY_X_CLIENT_ID,
      borderRadius: TURNKEY_BORDER_RADIUS,
      lightPrimaryHex: TURNKEY_LIGHT_PRIMARY_HEX,
      darkPrimaryHex: TURNKEY_DARK_PRIMARY_HEX,
      logoLight: LOGO_SRC_LIGHT_BACKGROUND,
      logoDark: LOGO_SRC_DARK_BACKGROUND
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children || $[2] !== isDark) {
    t2 = <SdkTurnkeyWebProvider config={t1} isDark={isDark} onAuthEvent={_temp2}>{children}</SdkTurnkeyWebProvider>;
    $[1] = children;
    $[2] = isDark;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}

// ── Active wallet bridge ──────────────────────────────────────────────────
function _temp2(name, payload) {
  if (name === "turnkey_popup_callback_skip") {
    reportPopupCallbackSkip({
      had_code: payload.had_code === true,
      had_id_token: payload.had_id_token === true
    });
  }
  if (name === "turnkey_session_reconcile_logout") {
    logger.warn("turnkey.session.reconcile_logout", payload);
  }
  if (name === "turnkey_refresh_failed") {
    logger.warn("turnkey.session.refresh_failed", {
      ...payload,
      auth_method: "embedded-wallet",
      boundary: "turnkey-signing-session",
      platform: "web"
    });
  }
  if (name === "auth_error") {
    reportLoginError(payload);
  }
  onEvent(name as Parameters<typeof onEvent>[0], payload as never);
}
function _temp() {
  return typeof document === "undefined" ? true : document.documentElement.classList.contains("dark");
}
function ActiveWalletBridgeInner(t0) {
  const $ = _c(7);
  const {
    children
  } = t0;
  const {
    activeWalletId,
    hasHydrated,
    setActiveWalletId
  } = useActiveWalletStore();
  let t1;
  if ($[0] !== activeWalletId || $[1] !== hasHydrated || $[2] !== setActiveWalletId) {
    t1 = {
      activeWalletId,
      isHydrated: hasHydrated,
      setActiveWalletId
    };
    $[0] = activeWalletId;
    $[1] = hasHydrated;
    $[2] = setActiveWalletId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== children || $[5] !== t1) {
    t2 = <ActiveWalletProvider value={t1}>{children}</ActiveWalletProvider>;
    $[4] = children;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
function reportActiveWalletRepairWarning(warning: {
  reason: 'persist_failed';
  persistedWalletType: 'embedded' | 'imported' | 'external' | 'missing';
  walletCount: number;
  eligibleWalletCount: number;
}): void {
  logger.warn('wallet.active_selection_repair_failed', warning);
}

// ── Impersonation bridge ──────────────────────────────────────────────────
// Reads the Zustand store and passes state + mutators to the SDK's
// SdkImpersonationProvider. App-specific callbacks (analytics, toast) are
// wired here as props.

// App-specific mutators + callbacks injected into the SDK's
// SdkImpersonationProvider. They close over no render state (they read/write
// the Zustand store + module singletons), so they live at module scope as
// stable singletons — no manual memo (FQ-140), and react-doctor prefers
// module-scope pure functions over per-render closures.
const IMPERSONATION_MUTATORS: ImpersonationStoreMutators = {
  setActive: (payload: ImpersonationActivatePayload): void => {
    useImpersonationStore.getState().setActive(payload);
  },
  setInactive: (reason: TeardownReason): void => {
    useImpersonationStore.getState().setInactive(reason);
  },
  setState: (patch: {
    expiresAt?: number;
    lastUsedAt?: number;
  }): void => {
    useImpersonationStore.setState(patch);
  }
};
function onImpersonationExpiredToast(): void {
  showToast({
    id: TOAST_IDS.IMPERSONATION_EXPIRED,
    message: translateToast('impersonation.toast.sessionExpired'),
    variant: 'warning'
  });
}
function onImpersonationRevokeError(message: string, reason: TeardownReason): void {
  logger.warn('impersonation.teardown.revoke_failed', {
    reason,
    message
  });
}
function ImpersonationInnerProvider(t0) {
  const $ = _c(11);
  const {
    children
  } = t0;
  const isActive = useImpersonationStore(_temp3);
  const grantToken = useImpersonationStore(_temp4);
  const grantId = useImpersonationStore(_temp5);
  const network = useImpersonationStore(_temp6);
  const target = useImpersonationStore(_temp7);
  const expiresAt = useImpersonationStore(_temp8);
  const abortController = useImpersonationStore(_temp9);
  let t1;
  if ($[0] !== abortController || $[1] !== expiresAt || $[2] !== grantId || $[3] !== grantToken || $[4] !== isActive || $[5] !== network || $[6] !== target) {
    t1 = {
      isActive,
      grantToken,
      grantId,
      network,
      target,
      expiresAt,
      abortController
    };
    $[0] = abortController;
    $[1] = expiresAt;
    $[2] = grantId;
    $[3] = grantToken;
    $[4] = isActive;
    $[5] = network;
    $[6] = target;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const storeState = t1;
  let t2;
  if ($[8] !== children || $[9] !== storeState) {
    t2 = <SdkImpersonationProvider state={storeState} mutators={IMPERSONATION_MUTATORS} onExpiredToast={onImpersonationExpiredToast} onRevokeError={onImpersonationRevokeError}>{children}</SdkImpersonationProvider>;
    $[8] = children;
    $[9] = storeState;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}

// ── Auth provider callbacks ───────────────────────────────────────────────
// Morfi-specific side-effects injected into the SDK's SdkAuthProvider.
function _temp9(s_5) {
  return s_5.abortController;
}
function _temp8(s_4) {
  return s_4.expiresAt;
}
function _temp7(s_3) {
  return s_3.target;
}
function _temp6(s_2) {
  return s_2.network;
}
function _temp5(s_1) {
  return s_1.grantId;
}
function _temp4(s_0) {
  return s_0.grantToken;
}
function _temp3(s) {
  return s.isActive;
}
type AuthSessionDiagnostic = Parameters<NonNullable<SdkAuthProviderCallbacks['onSessionDiagnostic']>>[0];
const AUTH_PROVIDER_CALLBACKS: SdkAuthProviderCallbacks = {
  onPreLogout: (): Promise<void> => teardownImpersonation('signout'),
  onSessionRestored: (userId: string): void => {
    onEvent('session_restored', {
      user_id: userId
    });
  },
  onSessionRestoreError: (error: string): void => {
    logger.error('Failed to restore session', {
      error
    });
    onEvent('session_restore_error', {
      error
    });
  },
  onSessionDiagnostic: (diagnostic: AuthSessionDiagnostic): void => {
    logger.warn('auth.session.diagnostic', {
      ...diagnostic
    });
  },
  onSessionRefresh: (event: {
    trigger: string;
    outcome: string;
  }): void => {
    onEvent('session_refresh', event);
  },
  onLogoutStart: (source: string): void => {
    if (source !== 'user') {
      logger.warn('auth.logout.triggered', {
        source,
        platform: 'web'
      });
    }
    showToast({
      id: TOAST_IDS.AUTH_LOGOUT_LOADING,
      message: translateToast('auth.toast.loggingOut'),
      variant: 'loading'
    });
  },
  onLogoutComplete: (userId: string): void => {
    onEvent('logout', {
      user_id: userId
    });
    dismissToast(TOAST_IDS.AUTH_LOGOUT_LOADING);
  },
  onLogoutError: (error: string): void => {
    logger.error('Logout request failed', {
      error
    });
    onEvent('logout_error', {
      error
    });
    dismissToast(TOAST_IDS.AUTH_LOGOUT_LOADING);
  },
  onResetOnboardingStore: (): void => {
    useOnboardingStore.getState().reset();
  },
  onClearNsfwRevealStore: (): void => {
    useNsfwRevealStore.getState().clear();
  },
  onResetIdentity: (): void => {
    resetIdentity();
  }
};

// ── NATS connect-failure → Better Stack ───────────────────────────────────
// The SDK's own OTLP translator is not installed in the web app (no browser
// OTLP endpoint — we ship the Better Stack RUM client instead), so the SDK's
// internal `sdkEvents` connect errors never reach Better Stack on their own.
// `onConnectError` is the deterministic seam the SDK fires on every NATS
// connect/auth failure; we forward it to `reportError` so a user whose
// realtime connection fails surfaces as a HARD error (not a console.error
// that never leaves the browser). The reason/phase live in the message AND
// as `nats_*` context columns so they're queryable on the dashboard.
type SdkConnectErrorInfo = Parameters<NonNullable<SDKProviderProps['onConnectError']>>[0];
function reportSdkConnectError(info: SdkConnectErrorInfo): void {
  reportError(new Error(`NATS ${info.stage} failed: ${info.reason} (${info.phase})`), {
    error_source: 'nats-connect',
    nats_reason: info.reason,
    nats_phase: info.phase,
    nats_stage: info.stage,
    nats_code: info.code,
    nats_attempt: info.attempt === undefined ? '' : String(info.attempt),
    nats_next_retry_ms: info.nextRetryMs === undefined || info.nextRetryMs === null ? '' : String(info.nextRetryMs),
    nats_is_auth: String(info.isAuth),
    nats_message: info.message
  });
}
function resolveProviderBlockhashMode(): BlockhashResolverMode {
  const raw = env.NEXT_PUBLIC_MORFI_BLOCKHASH_RESOLVER_MODE;
  return isBlockhashResolverMode(raw) ? raw : 'stream';
}

// ── Inner provider (pinned by network key) ────────────────────────────────

function SdkV2ProviderInner(t0) {
  const $ = _c(19);
  const {
    children,
    network
  } = t0;
  const config = useNetworkConfig();
  let t1;
  if ($[0] !== config.apiUrl || $[1] !== network) {
    t1 = () => createWebDefaultAdapter({
      cacheNamespace: `${network}:${toSdkApiBase(config.apiUrl)}`
    });
    $[0] = config.apiUrl;
    $[1] = network;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [adapter] = useState(t1);
  let t2;
  if ($[3] !== config.apiUrl) {
    t2 = process.env.NODE_ENV === "development" ? "" : toSdkApiBase(config.apiUrl);
    $[3] = config.apiUrl;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const apiUrl = t2;
  const identityKey = useSyncExternalStore(subscribeIdentity, getIdentitySnapshot, _temp10);
  const impersonatedViewerHash = useImpersonationStore(_temp11);
  const viewerScopeKey = impersonatedViewerHash === null ? identityKey : `impersonated:${impersonatedViewerHash}`;
  const t3 = config.apiKey ?? "";
  const t4 = config.natsUrl ?? "";
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = resolveProviderBlockhashMode();
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <SdkIdentityKeySync />;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== children || $[8] !== identityKey) {
    t7 = <MarketConfigProvider><AuthSyncProvider><SdkAuthProvider callbacks={AUTH_PROVIDER_CALLBACKS}><TurnkeyProviderInner><ActiveWalletBridgeInner><SdkWalletsProvider onActiveWalletRepairWarning={reportActiveWalletRepairWarning}><ImpersonationInnerProvider>{t6}<AnalyticsProvider clientKey={env.NEXT_PUBLIC_STATSIG_CLIENT_KEY}><IdentityProvider><GiftingHostProvider value={GIFTING_HOST_BINDINGS}><ChartIntentProvider identityKey={identityKey}>{children}</ChartIntentProvider></GiftingHostProvider></IdentityProvider></AnalyticsProvider></ImpersonationInnerProvider></SdkWalletsProvider></ActiveWalletBridgeInner></TurnkeyProviderInner></SdkAuthProvider></AuthSyncProvider></MarketConfigProvider>;
    $[7] = children;
    $[8] = identityKey;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== adapter || $[11] !== apiUrl || $[12] !== identityKey || $[13] !== network || $[14] !== t3 || $[15] !== t4 || $[16] !== t7 || $[17] !== viewerScopeKey) {
    t8 = <SDKProvider adapter={adapter} apiUrl={apiUrl} apiKey={t3} natsUrl={t4} identityKey={identityKey} viewerScopeKey={viewerScopeKey} networkScope={network} onConnectError={reportSdkConnectError} onNatsReady={reportNatsStartup} blockhashResolverMode={t5}>{t7}</SDKProvider>;
    $[10] = adapter;
    $[11] = apiUrl;
    $[12] = identityKey;
    $[13] = network;
    $[14] = t3;
    $[15] = t4;
    $[16] = t7;
    $[17] = viewerScopeKey;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
}

// Gift host bindings — the SDK gift hooks (`useSendUsdcGift`,
// `useSendOutcomeTokenGift`, `useClaimGift`, `useWithdrawGift`) delegate
// to these frontend-owned implementations. See
// `packages/backend-sdk-v2/src/platform/web/gifts/host-context.tsx` for
// the delegation rationale (TX builders stay in frontend; SDK owns the
// public name + type contract).
function _temp11(state) {
  return state.isActive ? state.targetUserIdHash : null;
}
function _temp10() {
  return null;
}
const GIFTING_HOST_BINDINGS: GiftingHostBindings = {
  // The SDK's binding types are an opaque DelegatedGiftMutationHook;
  // our concrete return shapes are richer (step/error/result/submit).
  // The runtime contract is identical — coerce via `unknown` so the
  // generic signature accepts both old and new hook shapes during the
  // migration. (Same pattern as the existing `useClaimReceipt` binding.)
  // ALLOW-UNKNOWN: SDK gifting host generic signature uses minimal hook shape; concrete returns are richer.
  useSendUsdcGift: useSendUsdcGift as unknown as GiftingHostBindings['useSendUsdcGift'],
  useSendOutcomeTokenGift:
  // ALLOW-UNKNOWN: SDK gifting host generic signature uses minimal hook shape; concrete returns are richer.
  useSendOutcomeTokenGift as unknown as GiftingHostBindings['useSendOutcomeTokenGift'],
  // ALLOW-UNKNOWN: SDK gifting host generic signature uses minimal hook shape; concrete returns are richer.
  useClaimGift: useClaimGift as unknown as GiftingHostBindings['useClaimGift'],
  // ALLOW-UNKNOWN: SDK gifting host generic signature uses minimal hook shape; concrete returns are richer.
  useWithdrawGift: useWithdrawGift as unknown as GiftingHostBindings['useWithdrawGift']
};

/**
 * Mounts `@morfi/backend-sdk-v2`'s `<SDKProvider>` for the web app and
 * composes all auth/wallet/turnkey providers internally.
 *
 * Keyed by the active network so a devnet↔mainnet toggle fully remounts
 * the SDK — a fresh adapter and a fresh boot / JWT mint against the
 * newly-selected network's `apiUrl` — rather than leaving the runtime
 * bound to the previous network.
 *
 * `layout.tsx` mounts only: `QueryProvider → NetworkSyncProvider → SdkV2Provider`.
 * Everything auth/wallet/turnkey is internal to this component.
 */
export function SdkV2Provider(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  const {
    network
  } = useUserNetwork();
  let t1;
  if ($[0] !== children || $[1] !== network) {
    t1 = <SdkV2ProviderInner key={network} network={network}>{children}</SdkV2ProviderInner>;
    $[0] = children;
    $[1] = network;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}